import { io, type Socket } from "socket.io-client";
import type { ClientToServerEvents, ServerToClientEvents } from "../../shared/events";

export type AppSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

// Assigned by connectSocket() once a lobby code is known - every UI module's
// init function only runs after that (see main.ts), so by the time anything
// actually calls `socket.emit(...)` this is guaranteed to be set despite
// starting undefined. A single stable `socket` reference lets every other
// client module keep importing and using it exactly as before.
export let socket!: AppSocket;

/** Connects to this lobby's own dynamic namespace. If the lobby has since been
 * reaped (or the code was never valid), the server's namespace-connect
 * validator rejects with "lobby-gone" - stop retrying and let the caller send
 * the user back to the lobby picker instead of retrying forever. */
export function connectSocket(lobbyCode: string, onLobbyGone: () => void): AppSocket {
  socket = io(`/lobby/${lobbyCode}`, {
    autoConnect: true,
    // Open the WebSocket straight away instead of Socket.IO's default of
    // starting on HTTP long-polling and upgrading afterwards - that saves
    // several round trips on every (re)connect and avoids the polling phase's
    // sticky-session pitfalls behind a proxy. tryAllTransports keeps polling
    // as an automatic fallback for networks that block WebSockets.
    transports: ["websocket", "polling"],
    tryAllTransports: true,
  });
  socket.on("connect_error", (err) => {
    if (err.message === "lobby-gone") {
      socket.io.reconnection(false);
      socket.disconnect();
      onLobbyGone();
    }
  });
  return socket;
}
